import { useCallback, useEffect, useMemo } from 'react';

import { fetchRoom, shipClientLogs } from 'mastodon/actions/rooms';
import type { ApiRoomJSON } from 'mastodon/api_types/rooms';
import { getAccessToken, me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { RoomAudioContext } from '../contexts/room_audio_context';
import type { RoomAudioContextValue } from '../contexts/room_audio_context';
import { useHostActivity } from '../hooks/useHostActivity';
import { useRoomAudio } from '../hooks/useRoomAudio';
import {
  usePollWithBackoff,
  pollErrorStatus,
} from '../hooks/usePollWithBackoff';
import { useRoomRecorder } from '../hooks/useRoomRecorder';
import { canRecordRoom, isUntracedRoom } from '../recording_policy';

interface Props {
  children: React.ReactNode;
}

export const RoomAudioProvider: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  // Select fields individually — subscribing to the whole rooms slice would
  // re-render this provider (and rebuild the audio context value's deps) on
  // every chat message and list-poll tick.
  const activeRoom = useAppSelector(
    (state) =>
      (state as unknown as { rooms: { activeRoom: ApiRoomJSON | null } }).rooms
        .activeRoom,
  );
  const participantVolumes = useAppSelector(
    (state) =>
      (
        state as unknown as {
          rooms: { participantVolumes: Record<string, number> };
        }
      ).rooms.participantVolumes,
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentAccountId = me!;
  const roomId = activeRoom?.id ?? null;
  const isHost = activeRoom
    ? activeRoom.host_account_id === currentAccountId
    : false;

  const recorder = useRoomRecorder();

  const handleRemoteStreamAdded = useCallback(
    (stream: MediaStream) => {
      recorder.addStream(stream);
    },
    [recorder],
  );

  const handleRemoteStreamRemoved = useCallback(
    (stream: MediaStream) => {
      recorder.removeStream(stream);
    },
    [recorder],
  );

  const {
    isMuted,
    forceMuted,
    toggleMute,
    speakingPeers,
    isLocalSpeaking,
    cleanup,
    getLocalStream,
    getRemoteStreams,
    isCameraOn,
    isScreenSharing,
    toggleCamera,
    toggleScreenShare,
    computerAudioVolume,
    setComputerAudioVolume,
    micGain,
    setMicGain,
    echoRisk,
    echoMuted,
    toggleEchoMute,
    audioBlocked,
    resumeAudio,
    masterVolume,
    setMasterVolume,
    localCameraTrack,
    localScreenTrack,
    remoteVideoTracks,
    selfObsPreview,
    setSelfObsPreview,
    logClient,
  } = useRoomAudio(
    roomId,
    activeRoom?.participants ?? [],
    currentAccountId,
    handleRemoteStreamAdded,
    handleRemoteStreamRemoved,
    participantVolumes,
  );

  // Ticket 69: the host's client reports that the host is taking part —
  // speaking, or publishing video. It lives HERE rather than in the room view
  // because this provider is mounted app-wide: a host who talks while browsing
  // elsewhere on the site (the mini player keeps their audio live) is very much
  // present, and reporting them away would hand their co-hosts End Room.
  useHostActivity(roomId ?? '', isHost && roomId !== null, {
    isSpeaking: isLocalSpeaking,
    isStreaming: isCameraOn || isScreenSharing,
  });

  // Start recording when host enters the room. Video rooms are exempt:
  // recordings of streams are heavy (video bandwidth/storage) and are
  // deferred to a future premium feature. Rooms that leave no trace — private
  // rooms and rooms in private communities alike — are never recorded; the
  // rule lives in canRecordRoom, mirroring Room#untraced? on the server.
  useEffect(() => {
    if (isHost && canRecordRoom(activeRoom) && !recorder.isRecording) {
      recorder.startRecording();
      void getLocalStream().then((localStream) => {
        recorder.addStream(localStream);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHost, roomId]);

  // The host can flip a live room to private mid-session. What was captured up
  // to that moment must be thrown away, not merely left paused — the room is
  // untraced from here on, and a half-recording is exactly the trace it is
  // meant not to leave.
  const untraced = isUntracedRoom(activeRoom);
  useEffect(() => {
    if (untraced && recorder.isRecording) {
      recorder.discardRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [untraced, recorder.isRecording]);

  // Poll active room state — every 3s while healthy, backing off (with
  // jitter) when the origin errors so a room full of viewers doesn't
  // stampede a struggling server (the 504/525 bursts during multi-person
  // streams). Failures and recoveries are shipped to the server's
  // [Room:Client] log: Cloudflare-edge errors (525) never reach origin
  // logs, so the client is the only witness.
  const pollRoomState = useCallback(
    () => (roomId ? dispatch(fetchRoom(roomId)) : Promise.resolve(null)),
    [dispatch, roomId],
  );
  const handleRoomPollError = useCallback(
    (err: unknown, consecutive: number) => {
      const status = pollErrorStatus(err);
      console.error(`[Room] POLL_FAIL room-state status=${status}`, err);
      // Ship only the first failure of a burst — every retry shipping its
      // own report would add load at the worst possible time.
      if (roomId && consecutive === 1) {
        void dispatch(
          shipClientLogs(roomId, [
            `[Room] API_ERROR poll=room_state status=${status}`,
          ]),
        );
      }
    },
    [dispatch, roomId],
  );
  const handleRoomPollRecover = useCallback(
    (failures: number) => {
      if (roomId) {
        void dispatch(
          shipClientLogs(roomId, [
            `[Room] API_ERROR_RECOVERED poll=room_state failures=${failures}`,
          ]),
        );
      }
    },
    [dispatch, roomId],
  );
  usePollWithBackoff(pollRoomState, 3_000, {
    onError: handleRoomPollError,
    onRecover: handleRoomPollRecover,
  });

  // Handle browser close / tab close — leave room via beacon
  useEffect(() => {
    if (!roomId) return;

    const handleBeforeUnload = () => {
      cleanup();
      // NOT sendBeacon: beacons cannot carry an Authorization header, and
      // the API is bearer-token-only — every beacon 401'd, ghosting the
      // participant row until the reaper caught it. keepalive fetch survives
      // page unload AND authenticates.
      const token = getAccessToken();
      if (!token) return;
      void fetch(`/api/v1/rooms/${roomId}/leave`, {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
      }).catch(() => {
        // Page is going away — the reaper is the backstop.
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [roomId, cleanup]);

  // Handle unmount cleanup (safety net)
  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo<RoomAudioContextValue | null>(() => {
    if (!activeRoom) return null;
    return {
      isMuted,
      forceMuted,
      toggleMute,
      speakingPeers,
      isLocalSpeaking,
      cleanup,
      getLocalStream,
      getRemoteStreams,
      recorder,
      isCameraOn,
      isScreenSharing,
      toggleCamera,
      toggleScreenShare,
      computerAudioVolume,
      setComputerAudioVolume,
      micGain,
      setMicGain,
      echoRisk,
      echoMuted,
      toggleEchoMute,
      audioBlocked,
      resumeAudio,
      masterVolume,
      setMasterVolume,
      localCameraTrack,
      localScreenTrack,
      remoteVideoTracks,
      selfObsPreview,
      setSelfObsPreview,
      logClient,
    };
  }, [
    activeRoom,
    isMuted,
    forceMuted,
    toggleMute,
    speakingPeers,
    isLocalSpeaking,
    cleanup,
    getLocalStream,
    getRemoteStreams,
    recorder,
    isCameraOn,
    isScreenSharing,
    toggleCamera,
    toggleScreenShare,
    computerAudioVolume,
    setComputerAudioVolume,
    micGain,
    setMicGain,
    echoRisk,
    echoMuted,
    toggleEchoMute,
    audioBlocked,
    resumeAudio,
    masterVolume,
    setMasterVolume,
    localCameraTrack,
    localScreenTrack,
    remoteVideoTracks,
    selfObsPreview,
    setSelfObsPreview,
    logClient,
  ]);

  return (
    <RoomAudioContext.Provider value={contextValue}>
      {children}
    </RoomAudioContext.Provider>
  );
};
